import { useContext, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import fuzzysort from "fuzzysort";
import { MaterialContext } from "../../context/MaterialContext";
import { UserContext } from "../../context/UserContext";

import inputStyle from "../../styles/input.module.scss";
import { closeIcon } from "../../components/Icons";

const optionsFuzzy = {
  limit: 10,
  allowTypo: false,
  threshold: -100,
  keys: ["title"],
};

function MaterialAdd() {
  const { allMaterials } = useContext(MaterialContext);
  const { userInformation } = useContext(UserContext);
  const [fuzzyResults, setFuzzyResults] = useState([]);
  const [input, setInput] = useState("");

  const { vendor, userType } = userInformation;
  const normalizedInput = input.trim().toLowerCase();
  const hasMinInput = normalizedInput.length >= 3;
  const createSlug = normalizedInput.replace(/\s+/g, "-");
  const vendorRestrictionCopy =
    vendor === "asiatan"
      ? "Asiatan users can only create or update Asiatan materials."
      : vendor === "tectuff"
        ? "TecTuff users must be logged in as a TecTuff vendor account to create or update TecTuff materials."
        : "";

  const vendorMaterials = useMemo(
    () => allMaterials.filter((item) => item.vendor === vendor),
    [allMaterials, vendor],
  );

  const duplicateCandidate = useMemo(
    () =>
      vendorMaterials.find(
        (item) =>
          item.slug === createSlug ||
          item.title?.toLowerCase() === normalizedInput,
      ),
    [vendorMaterials, createSlug, normalizedInput],
  );

  const handleInput = (e) => {
    setInput(e.target.value);
  };

  useEffect(() => {
    if (!hasMinInput) {
      setFuzzyResults([]);
      return;
    }

    const resultsFuzzy = fuzzysort.go(
      normalizedInput,
      vendorMaterials,
      optionsFuzzy,
    );
    setFuzzyResults(resultsFuzzy);
  }, [normalizedInput, hasMinInput, vendorMaterials]);

  const handleClear = () => {
    setInput("");
  };

  return (
    <div className="content">
      <div className={inputStyle.materialAddPage}>
        <div className={inputStyle.materialAddTopbar}>
          <div className={inputStyle.materialAddTopbarCopy}>
            <p className={inputStyle.materialAddKicker}>{userType} workspace</p>
            <h2>Material Workspace</h2>
            <p className={inputStyle.materialAddDescription}>
              Search existing materials to edit, or create a new one from the
              same screen.
            </p>
          </div>
          <Link className={inputStyle.materialAddTopbarAction} to="/library">
            Back to Library
          </Link>
        </div>

        <div className={inputStyle.materialAddCard}>
          <div className={inputStyle.materialAddWorkspace}>
            <section className={inputStyle.materialAddPane}>
              <div className={inputStyle.materialAddHeader}>
                <h3>Start Here</h3>
                <p>
                  Use this panel to search and stage your next material action.
                </p>
                {vendorRestrictionCopy ? (
                  <p className={inputStyle.materialAddRestriction}>
                    {vendorRestrictionCopy}
                  </p>
                ) : null}
              </div>

              <div className={inputStyle.materialAddGuide}>
                <h3>How this works</h3>
                <ol>
                  <li>Type at least 3 characters in Material name.</li>
                  <li>Select an existing material below to edit it.</li>
                  <li>Or create a new material from your current input.</li>
                </ol>
              </div>

              <div className={inputStyle.materialAddSearchBlock}>
                <label
                  className={inputStyle.materialAddLabel}
                  htmlFor="material-name"
                >
                  Material name
                </label>
                <div className={inputStyle.materialAddSearch}>
                  <input
                    id="material-name"
                    type="text"
                    placeholder="Start typing material name"
                    value={input}
                    onChange={handleInput}
                  />
                  {input && (
                    <span
                      className={inputStyle.materialAddClear}
                      onClick={handleClear}
                    >
                      {closeIcon}
                    </span>
                  )}
                </div>
                <p className={inputStyle.materialAddHint}>
                  Results appear after 3 characters.
                </p>
              </div>

              {!hasMinInput ? (
                <div className={inputStyle.materialAddEmptyState}>
                  <h4>Start by entering a material name</h4>
                  <p>Try examples like Antique Rave.</p>
                </div>
              ) : null}
            </section>

            <section className={inputStyle.materialAddPane}>
              {!hasMinInput ? (
                <div className={inputStyle.materialAddEmptySide}>
                  <h4>Ready when you are</h4>
                  <p>
                    Once you type at least 3 characters, you'll see matching
                    materials here and a quick create option beside them.
                  </p>
                </div>
              ) : (
                <>
                  <div className={inputStyle.materialAddCreateBlock}>
                    <div>
                      <h4>Create New</h4>
                      <p>
                        Create a new material entry using:{" "}
                        <strong>{input.trim()}</strong>
                      </p>
                      {duplicateCandidate ? (
                        <p className={inputStyle.materialAddWarning}>
                          A similar material already exists:{" "}
                          {duplicateCandidate.title}
                        </p>
                      ) : null}
                    </div>
                    <Link
                      className={inputStyle.materialAddPrimaryAction}
                      to={`/material/edit/${vendor}/${createSlug}`}
                    >
                      Create Material
                    </Link>
                  </div>

                  <div className={inputStyle.materialAddResultsHeader}>
                    <h4>Existing Materials</h4>
                    <span>
                      {fuzzyResults.length
                        ? `${fuzzyResults.length} match${fuzzyResults.length > 1 ? "es" : ""}`
                        : "No matches"}
                    </span>
                  </div>

                  {fuzzyResults.length === 0 ? (
                    <div className={inputStyle.materialAddNoResults}>
                      No existing materials found for this search. You can
                      create a new one above.
                    </div>
                  ) : null}

                  <ul className={inputStyle.materialAddResultList}>
                    {fuzzyResults?.map((item) => (
                      <li
                        className={inputStyle.materialAddResultItem}
                        key={item.obj.id}
                      >
                        <Link
                          to={`/material/edit/${item.obj.vendor}/${item.obj.slug
                            .toLowerCase()
                            .replace(/\s/g, "-")}`}
                        >
                          <div className={inputStyle.materialAddResultMain}>
                            <span>{item.obj.title}</span>
                            <span>{item.obj.vendor}</span>
                          </div>
                          <div className={inputStyle.materialAddResultMeta}>
                            <span>status: {item.obj.status || "-"}</span>
                            <span>
                              updated: {item.obj.date_modified || "-"}
                            </span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaterialAdd;
